import { Alert } from "@msuite/ui/alert";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import {
	getIbanCheckIssue,
	IBAN_CHECK_ISSUE_TEXT,
	countReviewWarnings,
} from "@msuite/utils/incoming-invoices";
import {
	type IncomingInvoiceReviewFormSchema,
	incomingInvoiceReviewFormSchema,
} from "@msuite/utils/schemas";
import { formatIban } from "@msuite/utils/strings";
import type { IncomingInvoiceDetail } from "@msuite/utils/types";
import { useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { formatInvoiceAmount } from "@/components/incoming-invoices";
import { IncomingInvoiceReviewForm } from "@/forms/incoming-invoice-review-form";
import { client } from "@/hono";
import { useCostCenters } from "@/hooks/use-cost-centers";
import { useDecideIncomingInvoiceApproval } from "@/hooks/use-decide-incoming-invoice-approval";
import { findAcceptanceError } from "./find-acceptance-error";
import { toApprovalDecision } from "./to-approval-decision";
import { toReviewFormValues } from "./to-review-form-values";

/** Props Interface */
interface ReviewIncomingInvoiceModalProps extends ModalProps {
	incomingInvoiceId: string;
	/** The caller's approval the review decides. */
	approvalId: string;
	/** A default approver deciding ahead of the open chain, having confirmed it. */
	skipChain: boolean;
}

/**
 * The "Prüfen" of an invoice: one step of its approval chain accepts it — with the cost
 * allocation and an optional Kürzung — or rejects it. Reads the invoice fresh when it opens, so
 * the allocation it starts from is the one the step before accepted.
 */
export function ReviewIncomingInvoiceModal({
	isOpen,
	onClose,
	incomingInvoiceId,
	approvalId,
	skipChain,
}: ReviewIncomingInvoiceModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { decide } = useDecideIncomingInvoiceApproval();
	const methods = useForm<IncomingInvoiceReviewFormSchema>({
		resolver: resolver(incomingInvoiceReviewFormSchema),
		defaultValues: async () => {
			const response = await client.incoming_invoices[":incomingInvoiceId"].$get({
				param: { incomingInvoiceId },
			});
			const data = await responseHandler(response);
			const invoice = data.incomingInvoice as IncomingInvoiceDetail;
			setIncomingInvoice(invoice);
			return toReviewFormValues(invoice);
		},
	});
	const state = useWatch({ control: methods.control, name: "state" });

	/** State */
	const [incomingInvoice, setIncomingInvoice] = useState<IncomingInvoiceDetail | null>(null);

	/** Query */
	const { costCenters, isLoading: isLoadingCostCenters } = useCostCenters();

	/** Functions */
	async function handleOnSubmit(values: IncomingInvoiceReviewFormSchema) {
		if (!incomingInvoice) return;
		const error = findAcceptanceError(values, incomingInvoice.netAmount, hasCostCenters);
		if (error?.kind === "reduction-too-high") {
			methods.setError(error.field, {
				message: t("incoming_invoices.decision.reduction_too_high"),
			});
			return;
		}
		if (error?.kind === "allocation-mismatch") {
			methods.setError(error.field, {
				message: t("incoming_invoices.decision.allocation_must_match", {
					amount: formatInvoiceAmount(error.netAmount, incomingInvoice.currency),
				}),
			});
			return;
		}
		await decide(
			incomingInvoiceId,
			approvalId,
			toApprovalDecision(values, { hasCostCenters, skipChain }),
		);
		onClose();
	}

	/** Computed */
	const hasCostCenters = costCenters.some((costCenter) => costCenter.isActive);
	const ibanIssue = incomingInvoice ? getIbanCheckIssue(incomingInvoice.checks) : null;
	// The allocation is set right here and the IBAN warns on its own, so neither counts.
	const failedCheckCount = countReviewWarnings(incomingInvoice?.checks ?? []);
	const isLoading = methods.formState.isLoading || isLoadingCostCenters;

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("incoming_invoices.decision.title")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={isLoading}
				width="48rem"
			>
				<Modal.Body>
					{ibanIssue && (
						<Alert
							variant={ibanIssue.kind === "new-iban" ? "warning" : "danger"}
							title={t(IBAN_CHECK_ISSUE_TEXT[ibanIssue.kind].title, {
								iban: formatIban(ibanIssue.iban ?? incomingInvoice?.iban ?? null),
							})}
							description={t("incoming_invoices.iban_check.approval_blocked")}
						/>
					)}
					{incomingInvoice && !isLoading && (
						<IncomingInvoiceReviewForm
							printedNetAmount={incomingInvoice.netAmount}
							currency={incomingInvoice.currency}
							isAcceptBlocked={!!ibanIssue}
							hasCostCenters={hasCostCenters}
							failedCheckCount={failedCheckCount}
						/>
					)}
				</Modal.Body>
				<Modal.FooterForm
					submitLabel={t(
						state === "rejected"
							? "incoming_invoices.decision.submit_reject"
							: "incoming_invoices.decision.submit_accept",
					)}
					submitButtonProps={{ variant: state === "rejected" ? "danger" : "primary" }}
				/>
			</Modal>
		</FormProvider>
	);
}
